import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";

interface MonthlyBudget {
    month: string;
    budgetConfigured: boolean;
    monthlyBudget: number | null;
    spentThisMonth: number;
    remaining: number | null;
    percentUsed: number | null;
    itemsThisMonth: number;
    status: 'NO_BUDGET' | 'WITHIN' | 'NEAR' | 'OVER';
}

interface Props {
    ownerId: string | null;
}

const money = (n: number | null | undefined): string =>
    n == null ? '—' : `${Math.round(n).toLocaleString('ru-RU')} ₽`;

const STATUS_META: Record<MonthlyBudget['status'], {bar: string; label: string; text: string}> = {
    WITHIN: {bar: 'bg-emerald-500', label: 'в рамках бюджета', text: 'text-emerald-600 dark:text-emerald-400'},
    NEAR: {bar: 'bg-amber-500', label: 'близко к лимиту', text: 'text-amber-600 dark:text-amber-400'},
    OVER: {bar: 'bg-rose-500', label: 'перерасход', text: 'text-rose-600 dark:text-rose-400'},
    NO_BUDGET: {bar: 'bg-gray-400', label: '', text: 'text-gray-500'},
};

/**
 * Самостоятельный блок контроля месячного бюджета: траты текущего месяца против бюджета из
 * стратегии. Грузит данные сам; некритичный — при ошибке не отображается.
 */
const BudgetTrackerSection: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<MonthlyBudget | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<MonthlyBudget>('/wardrobe-value/budget', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [ownerId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <section data-testid="budget-loading" className="animate-pulse">
                <div className="h-5 w-40 bg-gray-200 dark:bg-ink-700 rounded mb-3"/>
                <div className="h-16 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </section>
        );
    }

    if (error || !data) {
        return null;
    }

    if (!data.budgetConfigured) {
        return (
            <section data-testid="budget-not-configured" className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Бюджет месяца</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    В этом месяце потрачено {money(data.spentThisMonth)}. Задайте месячный бюджет в стратегии,
                    чтобы отслеживать лимит.
                </p>
                {ownerId == null && (
                    <Link
                        to="/strategy"
                        className="inline-flex min-h-[44px] items-center px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                        data-testid="budget-strategy-link"
                    >
                        Задать бюджет в стратегии
                    </Link>
                )}
            </section>
        );
    }

    const meta = STATUS_META[data.status];
    const barWidth = Math.min(100, data.percentUsed ?? 0);

    return (
        <section data-testid="budget-tracker" className="space-y-3">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Бюджет месяца</h2>
                <span className={`text-sm font-medium ${meta.text}`} data-testid={`budget-status-${data.status}`}>
                    {meta.label}
                </span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
                Потрачено <span className="font-semibold">{money(data.spentThisMonth)}</span> из {money(data.monthlyBudget)}
                {data.remaining != null && (
                    <span className="text-gray-400 dark:text-gray-500"> · осталось {money(data.remaining)}</span>
                )}
            </div>
            <div className="h-3 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden" data-testid="budget-bar">
                <div className={`h-full ${meta.bar}`} style={{width: `${barWidth}%`}}/>
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
                {data.percentUsed}% бюджета · {data.itemsThisMonth} покупок в этом месяце
            </div>
        </section>
    );
};

export default BudgetTrackerSection;
